import { EMPTY_TAGS_TEXT } from 'components/alias-pills';
import type { DataTableColumnDef } from 'components/data-table';

/**
 * A tags cell's content: the tags as the comma-delimited text the read sends, and `--` when there are
 * none, as the table's other empty cells read. Only the cell shows the dash: the column's value, which
 * exports and sorting read, stays blank.
 *
 * @param {unknown} value - The row's `crmTractTags` / `unitTags`.
 * @return {string} The tags, or `--`.
 */
export const tagsCellText = (value: unknown): string =>
  typeof value === 'string' && value !== '' ? value : EMPTY_TAGS_TEXT;

interface TagsTextColumnOptions {
  // A key of the row; a string, not `keyof T`, so a table generic over its row type (`T extends TractNRI`)
  // can use it too.
  accessorKey: string;
  header: string;
  id?: string;
  size?: number;
  enableColumnFilter?: boolean;
}

/**
 * The column of a table that shows a record's tags as comma text: `--` with none, wrapping, and never
 * sortable (the servers' sort allowlists have no tags column, so a click on its header is refused). Every
 * table built on `DataTableColumnDef` or a `columnHelper` takes its Tags column from here; a table only says
 * what it calls the column and where the text is. The map query's dataset columns
 * (`map-query/datasets/portfolio-assets.config.ts`) have their own column shape and set the same rules there.
 * @param {TagsTextColumnOptions} options - The accessor, the header, and optionally an id, a size and a column filter switch.
 * @return {DataTableColumnDef<T>} The column.
 */
export function tagsTextColumn<T>({
  accessorKey,
  header,
  id,
  size,
  enableColumnFilter,
}: TagsTextColumnOptions): DataTableColumnDef<T> {
  return {
    ...(id === undefined ? {} : { id }),
    accessorKey,
    header,
    ...(size === undefined ? {} : { size }),
    ...(enableColumnFilter === undefined ? {} : { enableColumnFilter }),
    enableSorting: false,
    meta: { type: 'text', wrap: true },
    cell: (info) => tagsCellText(info.getValue()),
  };
}
